import React, { useState, useEffect } from "react";
import { ArrowLeft, Clock, AlertCircle, ChevronDown, Info } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { request } from "../service/HttpService";
import { isRateLimited } from "../service/HttpErrors";
import { getJwtRole } from "../service/AuthService";
import { admin } from "../constants/Constants";
import { calculateOverallScore, sectionColor } from "../service/Operations";
import NetworkCanvas from "./NetworkCanvas";

function AnalysisGroups({ grouped, labelMap }) {
    const [open, setOpen] = useState({});
    const toggle = (key) => setOpen(s => ({ ...s, [key]: !s[key] }));
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {Object.entries(grouped).map(([scoreType, items]) => {
                const isOpen = !!open[scoreType];
                return (
                    <div key={scoreType} className="submissions-card">
                        <button className="analysis-group-header" onClick={() => toggle(scoreType)}>
                            <span>{labelMap[scoreType] ?? scoreType} Analysis</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span className="analysis-group-count">{items.length} {items.length === 1 ? "item" : "items"}</span>
                                <ChevronDown size={15} className={`analysis-chevron ${isOpen ? "analysis-chevron-open" : ""}`} />
                            </div>
                        </button>
                        {isOpen && (
                            <div className="analysis-items">
                                {items.map((item, i) => (
                                    <div key={item.id ?? i} className="analysis-item">
                                        {item.severity && (
                                            <span className={`analysis-severity severity-${item.severity.toLowerCase()}`}>
                                                {item.severity.charAt(0).toUpperCase() + item.severity.slice(1).toLowerCase()} Priority
                                            </span>
                                        )}
                                        <p className="analysis-issue">{item.issue}</p>
                                        {item.quote && <blockquote className="analysis-quote">{item.quote}</blockquote>}
                                        {item.suggestion && <p className="analysis-suggestion">{item.suggestion}</p>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default function AdminSubmission() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const id = searchParams.get("id");

    const [paper, setPaper] = useState(null);
    const [hasGraph, setHasGraph] = useState(false);
    const [similarPapers, setSimilarPapers] = useState([]);
    const [analyses, setAnalyses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const [smallScreen, setSmallScreen] = useState(window.innerWidth < 991);

    useEffect(() => {
        const onResize = () => {
            const small = window.innerWidth < 991;
            setSmallScreen(small);
            if (small) setLoading(false);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        if (getJwtRole() !== admin) {
            navigate("/");
            return;
        }
        if (!id) {
            navigate("/admin");
            return;
        }
        request("GET", `/admin/manuscript/${id}`)
            .then(res => {
                if (!res.success) {
                    navigate("/admin");
                    return;
                }
                setPaper(res.manuscript);
                setHasGraph(res.hasGraph ?? false);
                setAnalyses(res.analyses ?? []);
                setSimilarPapers(res.similarPapers ?? []);
            })
            .catch(err => isRateLimited(err) ? setLoadError(err.message) : navigate("/login"))
            .finally(() => setLoading(false));
    }, [id]);

    const [openTooltip, setOpenTooltip] = useState(null);
    const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (!openTooltip) return;
        const close = () => setOpenTooltip(null);
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, [openTooltip]);

    const handleInfoClick = (e, label) => {
        e.stopPropagation();
        if (openTooltip === label) { setOpenTooltip(null); return; }
        const rect = e.currentTarget.getBoundingClientRect();
        const tooltipWidth = 240;
        let left = rect.left + rect.width / 2 - tooltipWidth / 2;
        left = Math.max(12, Math.min(left, window.innerWidth - tooltipWidth - 12));
        setTooltipPos({ top: rect.bottom + 8, left });
        setOpenTooltip(label);
    };

    const scoreDescriptions = {
        Novelty: "How much new ground your paper breaks compared to existing literature.",
        Methodology: "How well the paper employs its methodological approach.",
        Impact: "The potential reach and significance of this research contribution.",
        Overall: "A combined score averaged across novelty, methodology, and impact.",
    };

    if (loadError) {
        return (
            <div className="home-layout">
                <Sidebar activePage="Admin" />
                <main className="home-main">
                    <Header />
                    <div className="home-content">
                        <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--ink-300)", fontSize: 13 }}>{loadError}</div>
                    </div>
                </main>
            </div>
        );
    }

    const scoreCards = paper ? [
        { label: "Novelty", value: paper.noveltyScore },
        { label: "Methodology", value: paper.methodScore },
        { label: "Impact", value: paper.impactScore },
        { label: "Overall", value: calculateOverallScore(paper) },
    ] : [];

    return (
        <div className="home-layout">
            <Sidebar activePage="Admin" />
            <main className="home-main">
                <Header />
                <div className="home-content">
                    <div className="upload-main">
                        <button className="chpw-back" onClick={() => navigate(-1)}>
                            <ArrowLeft size={14} />
                            Back
                        </button>

                        <div style={{ display: "inline-block", marginBottom: 8 }}>
                            <span style={{
                                fontSize: 10, fontWeight: 600, color: "var(--blue)",
                                background: "var(--blue-bg)", padding: "3px 10px", borderRadius: 6,
                                textTransform: "uppercase", letterSpacing: 0.5,
                            }}>Admin View</span>
                        </div>

                        <div className="upload-page-title-row">
                            <div style={{ flex: 1, minWidth: 0 }}>
                                {loading ? (
                                    <div className="skeleton" style={{ height: 22, width: "60%", marginBottom: 8 }} />
                                ) : (
                                    <>
                                        <h1 className="upload-page-heading">{paper?.title || paper?.name}</h1>
                                        {paper?.authors && <p className="upload-page-meta">{paper.authors}</p>}
                                        {paper?.uploadDate && (
                                            <p className="submission-upload-date">
                                                Uploaded {new Date(paper.uploadDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {(() => {
                            if (loading) return (
                                <div className="submission-scores">
                                    {Array.from({ length: 4 }).map((_, i) => (
                                        <div key={i} className="submission-score-card">
                                            <div className="skeleton" style={{ height: 11, width: "55%", margin: "0 auto 10px" }} />
                                            <div className="skeleton" style={{ height: 32, width: "42%", margin: "0 auto 8px" }} />
                                        </div>
                                    ))}
                                </div>
                            );

                            const status = paper?.status?.toLowerCase();
                            const isCompleted = status === "completed";
                            const isError = status === "error";

                            if (isError) return (
                                <div className="sub-status-banner sub-status-error">
                                    <AlertCircle size={18} />
                                    <div>
                                        <p className="sub-status-title">Processing failed</p>
                                        <p className="sub-status-msg">This manuscript encountered an error during processing.</p>
                                    </div>
                                </div>
                            );

                            if (!isCompleted) return (
                                <div className="sub-status-banner sub-status-processing">
                                    <Clock size={18} />
                                    <div>
                                        <p className="sub-status-title">Analysis in progress</p>
                                        <p className="sub-status-msg">This manuscript is being analyzed.</p>
                                    </div>
                                </div>
                            );

                            const analysisOrder = ["novelty", "methods", "impact"];
                            const analysisLabel = { novelty: "Novelty", methods: "Methodology", impact: "Impact" };
                            const analysisGrouped = analysisOrder.reduce((acc, type) => {
                                const items = analyses.filter(a => a.scoreType === type);
                                if (items.length > 0) acc[type] = items;
                                return acc;
                            }, {});

                            return (
                                <>
                                    <div className="submission-scores">
                                        {scoreCards.map((s) => (
                                            <div key={s.label} className="submission-score-card">
                                                <div className="submission-score-label-row">
                                                    {scoreDescriptions[s.label] && <span style={{ width: 11, flexShrink: 0 }} />}
                                                    <p className="submission-score-label">{s.label}</p>
                                                    {scoreDescriptions[s.label] && (
                                                        <button className="score-info-btn" onClick={e => handleInfoClick(e, s.label)}>
                                                            <Info size={11} />
                                                        </button>
                                                    )}
                                                </div>
                                                <p className="submission-score-value">{s.value}</p>
                                            </div>
                                        ))}
                                    </div>

                                    {Object.keys(analysisGrouped).length > 0 && (
                                        <AnalysisGroups grouped={analysisGrouped} labelMap={analysisLabel} />
                                    )}
                                </>
                            );
                        })()}

                        {/* Similar Papers */}
                        {!loading && paper?.status?.toLowerCase() === "completed" && (
                            <div className="submissions-card">
                                {hasGraph && !smallScreen && (
                                    <>
                                        <p className="similar-heading" style={{ padding: "16px 18px 0" }}>Research Network</p>
                                        <div className="submission-network-graph-full">
                                            <div style={{ borderRadius: "12px", height: "25vh", position: "relative", background: "#0f1a14", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                <NetworkCanvas />
                                            </div>
                                        </div>
                                    </>
                                )}
                                <p className="similar-heading" style={{ padding: "16px 18px 10px" }}>Top Similar Papers</p>
                                <table className="submissions-table similar-works-table">
                                    <thead>
                                        <tr>
                                            <th>Title</th>
                                            <th style={{ whiteSpace: "nowrap" }}>% Overlap</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {similarPapers.length > 0 ? similarPapers.map((p) => (
                                            <tr key={p.id} className="submission-row">
                                                <td>
                                                    <p className="similar-works-title-link" style={{ margin: 0 }}>{p.articleTitle}</p>
                                                    {p.section && (
                                                        <div style={{ marginTop: 4 }}>
                                                            <span className="similar-works-section-badge" style={sectionColor(p.section)}>{p.section}</span>
                                                        </div>
                                                    )}
                                                    {p.authors && <p className="submission-authors" style={{ marginTop: 4 }}>{p.authors}</p>}
                                                </td>
                                                <td className="similar-works-pct">{Math.round(p.similarity * 100)}%</td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan={2}><p className="settings-empty">No similar papers found.</p></td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {openTooltip && scoreDescriptions[openTooltip] && (
                <div
                    className="score-tooltip"
                    style={{ top: tooltipPos.top, left: tooltipPos.left }}
                    onClick={e => e.stopPropagation()}
                >
                    {scoreDescriptions[openTooltip]}
                </div>
            )}
        </div>
    );
}
